'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { HeroSlide, HeroStat } from '@/lib/cms';

interface HeroSectionProps {
  slides: HeroSlide[];
  stats: HeroStat[];
}

export default function HeroSection({ slides, stats }: HeroSectionProps) {
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = statsRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0');
      },
      { threshold: 0.1 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return (
    <header className="relative min-h-screen flex items-center overflow-hidden grain">
      {/* Slideshow Background */}
      <div className="absolute inset-0 z-0">
        {slides?.map((slide, i) => (
          <div key={slide.id} className="hero-slide absolute inset-0 w-full h-full">
            <AppImage
              src={slide?.src}
              alt={slide?.alt}
              fill
              className="object-cover object-center"
              priority={i === 0}
              quality={90}
            />
          </div>
        ))}
        {/* Multi-layer overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/50 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent z-10" />
      </div>
      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 pt-24 pb-48 w-full">
        <div className="max-w-2xl">
          {/* Label */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="text-xs font-medium tracking-widest text-white/90 uppercase">
              Certified Nail Artist · NYC
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-display text-5xl md:text-7xl font-medium text-white leading-[1.05] tracking-tight mb-6">
            Nail art that <span className="italic text-accent">speaks</span>
            <br />
            before you do.
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-lg mb-10 font-light">
            Bespoke designs crafted for the woman who treats beauty as self-expression. From
            editorial minimalism to hand-painted florals — every appointment is a custom experience.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#booking"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-white text-sm font-semibold rounded-full hover:bg-accent/90 transition-all duration-300 shadow-luxury"
            >
              Book an Appointment
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14m-7-7 7 7-7 7" />
              </svg>
            </a>
            <a
              href="#gallery"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white text-sm font-semibold rounded-full border border-white/25 hover:bg-white/20 transition-all duration-300 backdrop-blur-sm"
            >
              View Gallery
            </a>
          </div>
        </div>
      </div>
      {/* Floating Stats Card */}
      <div
        ref={statsRef}
        className="absolute bottom-8 left-6 right-6 md:left-12 md:right-auto z-20 opacity-100 translate-y-0 transition-all duration-700"
      >
        <div className="bg-surface rounded-3xl shadow-luxury p-5 md:p-7 flex flex-col md:flex-row gap-5 md:gap-10 items-start md:items-center">
          {stats?.map((stat, i) => (
            <div key={stat.id} className="flex items-center gap-3">
              {i > 0 && <div className="hidden md:block w-px h-10 bg-border" />}
              <div>
                <p className="text-xl font-display font-semibold text-primary tracking-tight">
                  {stat?.value}
                </p>
                <p className="text-xs text-muted uppercase tracking-wide font-medium">
                  {stat?.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/40 text-xs tracking-widest uppercase rotate-90 origin-center font-mono">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </header>
  );
}
